/**
 * One queue for everything the dashboards run.
 *
 * The server allows three concurrent queries per user and rejects the next
 * outright, and a dashboard has several reasons to run one at a time: the
 * tiles on the index, the main query behind a full view, and the lookups that
 * fill a filter's dropdown. Left to themselves they collide with each other
 * and with whatever the person is doing in the console.
 *
 * Two rather than three, so a slot is always left for the console — a
 * dashboard refreshing in another tab should never be the reason someone's own
 * Run is refused. Two rather than one because a full view now issues a main
 * query plus a lookup per filter; strictly serialising those made the page
 * visibly slow for no benefit.
 *
 * The slot is handed to the next waiter rather than released and re-taken:
 * incrementing after the await leaves a window where the count is stale, and a
 * task arriving inside it walks straight past a full gate.
 */
function createGate(limit: number) {
  let active = 0;
  const waiting: Array<() => void> = [];

  const release = () => {
    const next = waiting.shift();
    if (next) next();
    else active--;
  };

  return async function gate<T>(task: () => Promise<T>): Promise<T> {
    if (active >= limit) {
      await new Promise<void>((resolve) => waiting.push(resolve));
    } else {
      active++;
    }
    try {
      return await task();
    } finally {
      release();
    }
  };
}

export const dashboardGate = createGate(2);
